import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Activity, Clock, ShieldCheck, Lock, Coins } from 'lucide-react';
import { useModalA11y } from '../hooks/useModalA11y';

export default function GlobalPulseDrawer({
  isOpen,
  onClose,
  darkMode,
  onlineTotal,
  recentActivity,
  isVerified,
  ipBalance,
  streak,
  onViewActivity
}) {
  const containerRef = useModalA11y(isOpen, onClose);
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] xl:hidden"
          />

          {/* Drawer */}
          <motion.div
            ref={containerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Network Pulse"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={`fixed right-0 top-0 bottom-0 w-[85%] max-w-[360px] z-[210] xl:hidden shadow-2xl flex flex-col ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
          >
            {/* Header */}
            <div className={`p-6 border-b flex items-center justify-between ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-ping" />
                <h2 className="text-xs font-black uppercase tracking-[0.3em] text-gray-500">Network Pulse</h2>
              </div>
              <button
                onClick={onClose}
                aria-label="Close network pulse panel"
                className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-500' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8 no-scrollbar">
              {/* Online Counter */}
              <div className={`p-7 rounded-3xl border shadow-xl transition-all ${darkMode ? 'bg-gray-800/40 border-gray-800' : 'bg-gray-50/50 border-gray-100'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-2xs font-black text-gray-500 uppercase tracking-[0.2em]">Active Citizens</span>
                  <Activity size={16} className="text-primary-600" />
                </div>
                <div className="text-4xl font-black text-primary-600 tabular-nums tracking-tighter">
                  {(onlineTotal || 1).toLocaleString()}
                </div>
                <span className="text-2xs font-bold text-gray-500 uppercase tracking-tight opacity-60">Synchronized Nodes</span>
              </div>

              {/* Rewards Summary */}
              <div className={`p-7 rounded-3xl border shadow-xl transition-all ${darkMode ? 'bg-gray-800/40 border-gray-800' : 'bg-gray-50/50 border-gray-100'}`}>
                <div className="flex justify-between items-center mb-6">
                  <span className="text-2xs font-black text-gray-500 uppercase tracking-[0.2em]">Your Treasury</span>
                  {isVerified ? <ShieldCheck size={16} className="text-green-400" /> : <Lock size={16} className="text-amber-500" />}
                </div>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Coins size={14} className="text-primary-600" />
                            <span className="text-xs font-bold text-gray-500">Balance</span>
                        </div>
                        <span className="text-xl font-black tabular-nums">{ipBalance.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Zap size={14} className="text-pink-500" />
                            <span className="text-xs font-bold text-gray-500">Streak</span>
                        </div>
                        <span className="text-xl font-black tabular-nums text-pink-500">{streak}</span>
                    </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="space-y-4">
                <h3 className="text-2xs font-black text-gray-500 uppercase tracking-[0.2em]">Live Activity Log</h3>
                <div className="space-y-3">
                  {recentActivity && recentActivity.length > 0 ? (
                    recentActivity.map((p) => (
                      <div 
                        key={p.id}
                        onClick={() => { onViewActivity(p); onClose(); }}
                        className={`p-4 rounded-2xl border transition-all cursor-pointer group ${darkMode ? 'bg-gray-800/40 border-gray-800 hover:border-primary-600/40' : 'bg-gray-50/40 border-gray-100 hover:border-primary-600/20'}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">🗳️</span>
                          <div className="min-w-0">
                            <p className="text-2xs font-black text-primary-600 uppercase leading-none mb-1">Citizen Voted</p>
                            <p className={`text-xs font-bold truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {p.question}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-10 border-2 border-dashed rounded-3xl text-center opacity-40">
                      <Clock size={24} className="mx-auto mb-2" />
                      <p className="text-2xs font-bold uppercase tracking-widest">Scanning Network...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className={`p-6 border-t font-black uppercase tracking-[0.2em] text-2xs text-center opacity-40 ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              End-to-End Encrypted Pulse
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
